import React, { useState } from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { CONTACT } from '../mock';
import PageHeader from '../components/PageHeader';
import ReviewsSection from '../components/ReviewsSection';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';
import useSiteSettings from '../hooks/useSiteSettings';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      toast({ title: 'Message sent!', description: 'Thanks for reaching out. We\u2019ll get back to you soon.' });
      setForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not send message.' });
    } finally {
      setSubmitting(false);
    }
  };

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const settings = useSiteSettings();

  return (
    <main>
      <PageHeader title="Contact Us" breadcrumb="Contact Us" heroImage={settings.contactHero} />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">Contact Us</div>
            <h2 className="section-title text-3xl md:text-5xl mb-4">Craving a Donair?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Whether you’re placing a large group order, asking about our ingredients, or just want to say hi — we’d love to hear from you!</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <a href={`tel:${CONTACT.phoneRaw}`} className="bg-[#FDF3EC] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><Phone size={22} /></div>
              <h3 className="text-[#ED2E2E] font-bold text-lg mb-1">Phone</h3>
              <p className="text-[#0F1C2E] font-semibold">{CONTACT.phone}</p>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="bg-[#FDF3EC] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><Mail size={22} /></div>
              <h3 className="text-[#ED2E2E] font-bold text-lg mb-1">Email</h3>
              <p className="text-[#0F1C2E] font-semibold">{CONTACT.email}</p>
            </a>
            <a href={CONTACT.mapUrl} target="_blank" rel="noopener noreferrer" className="bg-[#FDF3EC] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><MapPin size={22} /></div>
              <h3 className="text-[#ED2E2E] font-bold text-lg mb-1">Location</h3>
              <p className="text-[#0F1C2E] font-semibold text-sm">{CONTACT.address}</p>
            </a>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h3 className="font-heading font-bold text-2xl text-[#0F1C2E] mb-4">Find Us On:</h3>
              <div className="flex gap-3 mb-8">
                <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#ED2E2E] hover:bg-[#C81E1E] text-white flex items-center justify-center transition-colors">
                  <Facebook size={18} />
                </a>
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#ED2E2E] hover:bg-[#C81E1E] text-white flex items-center justify-center transition-colors">
                  <Instagram size={18} />
                </a>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-80">
                <iframe title="map" className="w-full h-full" src="https://www.google.com/maps?q=3353+Coast+Meridian+Rd,+Port+Coquitlam,+BC&output=embed" allowFullScreen />
              </div>
            </div>

            <form onSubmit={onSubmit} className="bg-[#FDF3EC] rounded-3xl p-8 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F1C2E] mb-1">First Name *</label>
                  <input required value={form.firstName} onChange={on('firstName')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 transition" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0F1C2E] mb-1">Last Name *</label>
                  <input required value={form.lastName} onChange={on('lastName')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 transition" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F1C2E] mb-1">Email Address *</label>
                <input type="email" required value={form.email} onChange={on('email')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 transition" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F1C2E] mb-1">Contact Number *</label>
                <input required value={form.phone} onChange={on('phone')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 transition" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F1C2E] mb-1">Comments / Message *</label>
                <textarea required rows={5} value={form.message} onChange={on('message')} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:border-[#ED2E2E] focus:ring-2 focus:ring-[#ED2E2E]/20 transition resize-none" />
              </div>
              <button type="submit" disabled={submitting} className="btn-wrappus w-full justify-center disabled:opacity-60">
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <ReviewsSection />
    </main>
  );
}
